import { notification } from 'antd';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment-timezone';
import { getIntl, getLocale, history, setLocale } from 'umi';

import {
  accountConfirm,
  authLogin,
  authLogout,
  authPartyLogin,
  authRefreshToken,
  sendTwoFactorAuthCode,
  validateSecurityCode,
  validateSsoClient,
} from '@/services/login';
import { setAuthority } from '@/utils/infrastructure/auth';
import { authRedirect } from '@/utils/infrastructure/utilities';
import { getDefaultLocale } from '@/utils/infrastructure/utilities';

const portalStorageKey = 'x-auth-companyPortalConfig';
const twoFactorStorageKey = 'x-auth-twoFactorState';

const twoFactor = {
  required: false,
  codeSent: false,
  providers: [],
};

const initialState = {
  status: undefined,
  accountConfirmed: false,
  twoFactor,
  auth: false,
};

// Function to restore twoFactor state from localStorage
const restoreTwoFactorState = () => {
  try {
    const savedState = localStorage.getItem(twoFactorStorageKey);
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (e) {
    console.error('Error restoring twoFactor state:', e);
  }
  return twoFactor;
};

// Function to save twoFactor state to localStorage
const saveTwoFactorState = (state) => {
  try {
    localStorage.setItem(twoFactorStorageKey, JSON.stringify(state));
  } catch (e) {
    console.error('Error saving twoFactor state:', e);
  }
};

// Function to clear twoFactor state from localStorage
const clearTwoFactorState = () => {
  try {
    localStorage.removeItem(twoFactorStorageKey);
  } catch (e) {
    console.error('Error clearing twoFactor state:', e);
  }
};

// Function to clear all SSO-related flags from localStorage
const clearSsoState = () => {
  try {
    localStorage.removeItem('sso-redirect-url');
    localStorage.removeItem('sso-client-id');
    localStorage.removeItem('sso-automatic-flow');
    localStorage.removeItem('sso-provider');
    localStorage.removeItem('sso-auto-disabled');
    localStorage.removeItem('sso-client-validated');
    localStorage.removeItem('sso-session-id');
  } catch (e) {}
};

const Model = {
  namespace: 'auth',
  state: {
    ...initialState,
    twoFactor: restoreTwoFactorState(),
  },
  effects: {
    *login({ payload }, { call, put, select }) {
      try {
        const token = yield call(authLogin, payload);

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (token.status === 206 || token.code === '206' || token.status === 2) &&
          token.ssoRequired &&
          token.exchangeCode
        ) {
          const redirectUrl = localStorage.getItem('sso-redirect-url');

          if (redirectUrl) {
            clearSsoState();
            clearTwoFactorState();

            const finalUrl = `${redirectUrl}?code=${token.exchangeCode}`;
            window.location.href = finalUrl;
            return { success: true };
          }
        }

        if (history.location.state) {
          localStorage.setItem(portalStorageKey, JSON.stringify(history.location.state));
        }

        if (payload.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(payload.accessToken),
          });
        }

        yield put({
          type: 'changeLoginStatus',
          payload: {
            ...token,
            ...(_.has(token, 'twoFactorRequired') && { ...payload }),
          },
        });

        // Update authority after reducer completes
        yield put({ type: 'updateAuthorityAfterLogin', payload: token });

        // Update locale after reducer completes
        yield put({ type: 'updateLocaleAfterLogin', payload: token });

        // Return success result
        return { success: true };
      } catch (e) {
        const portal = yield select((o) => o.companyPortal.portal);
        history.replace({
          pathname: '/user/login',
          ...(portal && { query: { portal: portal.id } }),
        });

        // Return a result indicating failure instead of throwing
        return { success: false, error: e };
      }
    },

    *validateSsoClient({ payload }, { call, put, select }) {
      try {
        yield call(validateSsoClient, payload);
        return { success: true };
      } catch (e) {
        console.error('SSO client validation failed:', e);
        return { success: false, error: e };
      }
    },

    *oauth2Login({ payload }, { call, put, select }) {
      try {
        const res = yield call(authPartyLogin, payload);

        // Check if this is an SSO response (status 206)
        if (res.status === 206 || res.code === '206' || res.status === 2) {
          if (res.ssoRequired && res.exchangeCode) {
            const redirectUrl = localStorage.getItem('sso-redirect-url');

            if (redirectUrl) {
              clearSsoState();
              clearTwoFactorState();

              const finalUrl = `${redirectUrl}?code=${res.exchangeCode}`;
              window.location.href = finalUrl;
              return;
            }
          }
        }

        yield put({ type: 'changeLoginStatus', payload: res });

        // Update authority after reducer completes
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });

        // Update locale after reducer completes
        yield put({ type: 'updateLocaleAfterLogin', payload: res });

        const portal = localStorage.getItem(portalStorageKey);
        if (portal) localStorage.setItem(portalStorageKey, portal);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }
            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }
            window.location.href = url.toString();
            return;
          } catch (err) {
            console.error('Invalid SSO redirect URL:', err);
          }
        }

        window.location.href = history.location?.query?.redirect || '/';
      } catch (e) {
        console.error('oauth2Login error:', e);

        // Clean up SSO data on error but keep validation status
        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');
        const ssoClientId = localStorage.getItem('sso-client-id');
        const ssoProvider = localStorage.getItem('sso-provider');

        localStorage.removeItem('sso-automatic-flow');
        localStorage.removeItem('sso-provider');
        localStorage.setItem('sso-auto-disabled', 'true');

        const portal = yield select((state) => state.companyPortal.portal);
        const queryParams = [];
        if (portal) {
          queryParams.push(`portal=${portal.id}`);
        }
        if (ssoClientId) {
          queryParams.push(`clientId=${encodeURIComponent(ssoClientId)}`);
        }
        if (ssoRedirectUrl) {
          queryParams.push(`redirectUrl=${encodeURIComponent(ssoRedirectUrl)}`);
        }
        if (ssoProvider) {
          queryParams.push(`provider=${encodeURIComponent(ssoProvider)}`);
        }

        const query = queryParams.length ? `?${queryParams.join('&')}` : '';

        history.replace(`/user/login${query}`, history.location?.state);
      }
    },

    *sendTwoFactorCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);

        // Only send the necessary data, avoiding circular references
        const requestData = {
          twoFactorProvider: payload.twoFactorProvider?.value || payload.twoFactorProvider,
          username: data.username,
          password: data.password,
        };

        yield call(sendTwoFactorAuthCode, requestData);

        const newTwoFactorState = { ...data, ...payload, codeSent: true };
        saveTwoFactorState(newTwoFactorState);

        yield put({
          type: 'queryTwoFactorCode',
          payload: newTwoFactorState,
        });
      } catch (e) {}
    },

    *validateCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        const ssoClientId = localStorage.getItem('sso-client-id');
        const isSsoFlow = !!ssoClientId && !!localStorage.getItem('sso-redirect-url');

        const res = yield call(validateSecurityCode, {
          ...payload,
          ...data,
          ...(isSsoFlow && {
            isSsoFlow: true,
            ssoClientId,
          }),
        });

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (res.status === 206 || res.code === '206' || res.status === 2) &&
          res.ssoRequired &&
          res.exchangeCode
        ) {
          const redirectUrl = localStorage.getItem('sso-redirect-url');

          if (redirectUrl) {
            clearSsoState();
            clearTwoFactorState();

            const finalUrl = `${redirectUrl}?code=${res.exchangeCode}`;
            window.location.href = finalUrl;
            return;
          }
        }

        yield put({ type: 'changeLoginStatus', payload: res });

        // Update authority after reducer completes
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });

        // Update locale after reducer completes
        yield put({ type: 'updateLocaleAfterLogin', payload: res });

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }
        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }
            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }
            window.location.href = url.toString();
            return;
          } catch (err) {
            console.error('Invalid SSO redirect URL:', err);
          }
        }

        authRedirect(res);
      } catch (e) {}
    },

    *refreshToken({}, { call, put }) {
      try {
        const token = localStorage.getItem('x-auth-token');
        const res = yield call(authRefreshToken, token);

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        yield put({ type: 'changeLoginStatus', payload: res });

        // Update authority after reducer completes
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });

        // Update locale after reducer completes
        yield put({ type: 'updateLocaleAfterLogin', payload: res });

        yield put({ type: 'user/fetchCurrent', payload: true });
      } catch (e) {}
    },

    *updateAuthorityAfterLogin({ payload }, { call }) {
      try {
        if (payload.accessToken) {
          const user = jwtDecode(payload.accessToken);
          setAuthority(user.role);
        }
      } catch (e) {
        console.error('Error updating authority:', e);
      }
    },

    *updateLocaleAfterLogin({ payload }, { call }) {
      try {
        if (payload.accessToken) {
          const locale = getDefaultLocale(payload);
          setLocale(locale, false);
          moment.locale(locale);
        }
      } catch (e) {
        console.error('Error updating locale:', e);
      }
    },

    *cleanupTwoFactor(o, { put }) {
      clearTwoFactorState();
      yield put({ type: 'cleanTwoFactor' });
    },

    *cleanConfirmAccount(o, { put }) {
      yield put({ type: 'cleanAccountConfirm' });
    },

    *confirmAccount({ payload }, { call, put, select }) {
      try {
        const res = yield call(accountConfirm, payload);
        if (res?.message) {
          notification.success({
            message: getIntl().formatMessage({ id: 'user.twoFactor.alert.title' }),
            description: res.message,
          });
        }
        yield put({ type: 'queryConfirm', payload: true });
      } catch (e) {
        const portal = yield select((state) => state.companyPortal.portal);
        history.replace(
          {
            pathname: '/user/login',
            ...(portal && {
              query: { portal: portal.id },
            }),
          },
          history.location?.state || portal,
        );
      }
    },

    *logout(o, { call, put, select }) {
      if (window.location.pathname !== '/user/login') {
        // Set a temporary flag to indicate logout is in progress BEFORE any async operations
        localStorage.setItem('x-logout-in-progress', 'true');

        try {
          yield call(authLogout);
          yield put({ type: 'auth/cleanup' });
          yield put({ type: 'user/clearCurrentUser' });

          // Clean up all data from current login
          localStorage.removeItem('x-auth-token');
          localStorage.removeItem('antd-pro-authority');
          localStorage.removeItem('x-auth-user');
          localStorage.removeItem('x-userContext');
          clearTwoFactorState();

          let loginUrl = '/user/login';
          let portal = localStorage.getItem(portalStorageKey);

          if (portal) {
            try {
              portal = JSON.parse(portal);
              loginUrl += `?portal=${portal.id}`;
              yield put({
                type: 'companyPortal/auth',
                payload: _.pick(portal, ['companyCode', 'portalCode']),
              });
            } catch (e) {}
          }

          // Go back to login
          history.replace(loginUrl, history.location.state);
        } finally {
          // Clear the logout flag after a longer delay to ensure all async operations complete
          setTimeout(() => {
            localStorage.removeItem('x-logout-in-progress');
          }, 2000);
        }
      }
    },

    *cleanup(__, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    changeLoginStatus(state, { payload }) {
      try {
        if (payload.accessToken) {
          const user = jwtDecode(payload.accessToken);

          localStorage.setItem('x-user-timezone', user.timeZone);
          localStorage.setItem(
            'x-auth-token',
            JSON.stringify({ ...payload, issuedAt: Date.now() }),
          );

          moment.tz.setDefault(user.timeZone);
        }
      } catch (e) {}

      const newState = {
        ...state,
        status: payload.status,
        auth: !!payload.accessToken,
        type: payload.type,

        ...(payload.twoFactorRequired && {
          twoFactor: {
            ...state.twoFactor,
            required: payload.twoFactorRequired,
            providers: _.map(payload.twoFactorProviders, (str) => ({
              label: `user.twoFactor.provider.${str}`,
              value: str,
            })),
            ..._.pick(payload, ['password', 'username']),
          },
        }),
        ...(payload.accessToken && { twoFactor }),
      };

      // Save twoFactor state if required
      if (payload.twoFactorRequired) {
        saveTwoFactorState(newState.twoFactor);
      } else if (payload.accessToken) {
        clearTwoFactorState();
      }

      return newState;
    },

    queryTwoFactorCode(state, { payload }) {
      const newTwoFactorState = {
        ...state.twoFactor,
        ...payload,
      };

      saveTwoFactorState(newTwoFactorState);

      return {
        ...state,
        twoFactor: newTwoFactorState,
      };
    },

    queryConfirm(state, { payload }) {
      return {
        ...state,
        accountConfirmed: payload,
      };
    },

    cleanTwoFactor(state) {
      clearTwoFactorState();
      return {
        ...state,
        twoFactor,
      };
    },

    cleanAccountConfirm(state) {
      return {
        ...state,
        accountConfirmed: false,
      };
    },

    reset() {
      clearTwoFactorState();
      return initialState;
    },
  },
};

export default Model;
